import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'react-hot-toast'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Badge,
  Button,
} from '@goodday-clone/ui'
import {
  BadgeDollarSign,
  Kanban,
  Clock,
  Archive,
  Plus,
  RefreshCw,
  X,
  CheckCircle2,
  Rocket,
  ArrowUpRight,
  ExternalLink,
  Search,
  Bookmark,
  ChevronDown,
  SlidersHorizontal,
  Check,
  FileText,
  LayoutGrid,
  List,
  ArrowLeft,
  FileSpreadsheet,
} from 'lucide-react'
import { DealDetailModal, DealDetailData } from '@/components/sales/deal-detail-modal'
import { AddDealModal } from '@/components/sales/add-deal-modal'
import { ImportExcelModal } from '@/components/sales/import-excel-modal'
import { DealsTable } from '@/components/sales/DealsTable'
import { api } from '@/api/client'
import { useDealStages } from '@/shared/hooks/useCrmVocabulary'
import { useAuth } from '@/context/AuthContext'
import { useDealSpaceAccess } from '@/shared/hooks/useDealSpaceAccess'
import { createProjectFromDeal, fetchLinkedProjects, LinkedProjectInfo } from '@/lib/deal-project-utils'
import { groupServicesByCategory } from '@/shared/constants/services'
import { DealQuotationLink } from '@/components/sales/DealQuotationLink'

export interface Deal {
  id: string
  title: string
  client: string
  stage: string
  value: number
  probability: number
  owner?: string | null
  priority?: string | null
  closeDate?: string | null
  notes?: string | null
  services?: string[]
  organizationId?: string
  createdAt?: string
  updatedAt?: string
  contactPerson?: string
  contactDesignation?: string
  /** The quotation raised for this deal, if any (set by the API). */
  quotation?: { id: string; number: string; status: string } | null
  /** The deal is won, or its advance payment is in, so a delivery project may be launched (set by the API). */
  canLaunchProject?: boolean
  contactEmail?: string
  contactPhone?: string
  source?: string
  number?: string | number
  address?: string
  billingAddress?: string
  clientAddress?: string
  gstin?: string
  clientGstin?: string
  pinned?: boolean
}

const PRIORITY_OPTIONS = [
  'Highest',
  'High',
  'Normal',
  'Low',
  'Lowest',
]

export default function DealsPage() {
  const { data: dealStages = [] } = useDealStages()
  const wonStageNames = useMemo(() => dealStages.filter((s) => s.isWon).map((s) => s.name), [dealStages])
  const isDealWon = useCallback((stageName?: string) => {
    const s = (stageName || '').toLowerCase()
    return s === 'closed won' || wonStageNames.some((w) => w.toLowerCase() === s)
  }, [wonStageNames])
  const [deals, setDeals] = useState<Deal[]>([])
  const [loading, setLoading] = useState(true)
  const [isAddModalOpen, setIsAddModalOpen] = useState(false)
  const [isImportModalOpen, setIsImportModalOpen] = useState(false)
  const [selectedDeal, setSelectedDeal] = useState<DealDetailData | null>(null)
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false)
  const [notification, setNotification] = useState<string | null>(null)
  const [lastCreatedProjectId, setLastCreatedProjectId] = useState<string | null>(null)
  const { organization } = useAuth()
  const { canCreateSpace, canViewSpace } = useDealSpaceAccess()
  const [linkedProjects, setLinkedProjects] = useState<Map<string, LinkedProjectInfo>>(new Map())
  const [creatingDealId, setCreatingDealId] = useState<string | null>(null)

  // Filter States matching toolbar design
  const [searchQuery, setSearchQuery] = useState('')
  const [assigneeFilter, setAssigneeFilter] = useState('all')
  const [stageFilter, setStageFilter] = useState('all')
  const [priorityFilter, setPriorityFilter] = useState('all')
  const [pinFilter, setPinFilter] = useState<'all' | 'pinned' | 'unpinned'>('all')
  const [activeView, setActiveView] = useState('all')

  // Table is the default; the choice is remembered. Narrow screens always use cards.
  const [viewMode, setViewMode] = useState<'table' | 'cards'>(() => {
    try {
      return localStorage.getItem('deals:viewMode') === 'cards' ? 'cards' : 'table'
    } catch {
      return 'table'
    }
  })
  const chooseViewMode = (mode: 'table' | 'cards') => {
    setViewMode(mode)
    try {
      localStorage.setItem('deals:viewMode', mode)
    } catch {}
  }
  const [isNarrow, setIsNarrow] = useState(
    () => typeof window !== 'undefined' && !!window.matchMedia?.('(max-width: 767px)').matches
  )
  useEffect(() => {
    const query = window.matchMedia?.('(max-width: 767px)')
    if (!query) return
    const onChange = () => setIsNarrow(query.matches)
    query.addEventListener('change', onChange)
    return () => query.removeEventListener('change', onChange)
  }, [])
  const effectiveView = isNarrow ? 'cards' : viewMode

  // Who a deal can be reassigned to in bulk (the same people the Add Deal form offers).
  const [assignableUsers, setAssignableUsers] = useState<Array<{ id: string; name: string }>>([])
  useEffect(() => {
    api
      .get<{ data: Array<{ id: string; name: string }> }>('/users/assignable?for=leads')
      .then((res) => setAssignableUsers(res?.data || []))
      .catch(() => setAssignableUsers([]))
  }, [])
  const [isViewsOpen, setIsViewsOpen] = useState(false)
  const viewsDropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (viewsDropdownRef.current && !viewsDropdownRef.current.contains(e.target as Node)) {
        setIsViewsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const refreshLinkedProjects = async () => {
    const map = await fetchLinkedProjects(organization?.id)
    setLinkedProjects(map)
  }

  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const handleCreateQuotation = (dealItem: Deal) => {
    let addr = dealItem.clientAddress || dealItem.address || dealItem.billingAddress || ''
    let gst = dealItem.clientGstin || dealItem.gstin || ''
    if (!addr && dealItem.notes) {
      const am = dealItem.notes.match(/(?:Address|Billing Address|Location):\s*([^,\n\)]+)/i)
      if (am) addr = am[1].trim()
    }
    if (!gst && dealItem.notes) {
      const gm = dealItem.notes.match(/(?:GSTIN|GST|Tax ID):\s*([A-Z0-9]{15})/i)
      if (gm) gst = gm[1].trim()
    }

    const payload = {
      dealId: dealItem.id,
      title: dealItem.title,
      clientName: dealItem.client,
      clientAddress: addr,
      clientGstin: gst,
      contactPerson: dealItem.contactPerson || '',
      contactDesignation: dealItem.contactDesignation || '',
      email: dealItem.contactEmail || '',
      phone: dealItem.contactPhone || '',
      value: dealItem.value || 0,
      notes: dealItem.notes || '',
      description: dealItem.notes || '',
      services: dealItem.services || [],
      owner: dealItem.owner || '',
      closeDate: dealItem.closeDate || '',
      stage: dealItem.stage || 'Closed Won',
    }

    const searchParams = new URLSearchParams({
      new: 'quotation',
      dealId: dealItem.id,
      clientName: dealItem.client || '',
      title: dealItem.title || '',
      contactPerson: dealItem.contactPerson || '',
      contactDesignation: dealItem.contactDesignation || '',
      email: dealItem.contactEmail || '',
      phone: dealItem.contactPhone || '',
      value: String(dealItem.value || 0),
    })

    if (addr) searchParams.set('clientAddress', addr)
    if (gst) searchParams.set('clientGstin', gst)
    if (dealItem.services && dealItem.services.length > 0) {
      searchParams.set('services', dealItem.services.join(','))
    }

    navigate(`/app/sales/quotations?${searchParams.toString()}`, {
      state: { fromDeal: payload },
    })
  }

  const mapRawDeal = (d: any) => ({
    id: d._id || d.id,
    title: d.title,
    client: d.client,
    stage: d.stage || 'Discovery',
    priority: d.priority || 'Normal',
    value: typeof d.value === 'number' ? d.value : parseFloat(d.value) || 0,
    probability: typeof d.probability === 'number' ? d.probability : parseInt(d.probability, 10) || 50,
    owner: d.owner || 'Bob Smith',
    contactPerson: d.contactPerson || '',
    contactDesignation: d.contactDesignation || '',
    quotation: d.quotation || null,
    canLaunchProject: Boolean(d.canLaunchProject),
    contactEmail: d.contactEmail || '',
    contactPhone: d.contactPhone || '',
    closeDate: d.closeDate || 'Q4 2026',
    notes: d.notes || '',
    services: d.services || [],
    address: d.address || d.billingAddress || d.clientAddress || '',
    billingAddress: d.billingAddress || d.address || '',
    clientAddress: d.clientAddress || d.address || '',
    gstin: d.gstin || d.clientGstin || '',
    source: d.source || '',
    pinned: Boolean(d.pinned),
  })

  const fetchDeals = async () => {
    setLoading(true)
    try {
      const data = await api.get<any>('/deals')
      const dealList = Array.isArray(data) ? data : (data?.deals || data?.data || [])
      setDeals(dealList.map(mapRawDeal))
    } catch (err) {
      console.error('Failed to load deals:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchDeals()
    refreshLinkedProjects()

    const handleProjCreated = () => refreshLinkedProjects()
    window.addEventListener('projectCreated', handleProjCreated)
    return () => window.removeEventListener('projectCreated', handleProjCreated)
  }, [organization?.id])

  // Derive unique assignees from loaded deals
  const uniqueAssignees = useMemo(() => {
    const set = new Set<string>()
    deals.forEach((d) => {
      if (d.owner && d.owner.trim()) set.add(d.owner.trim())
    })
    return Array.from(set).sort()
  }, [deals])

  // Filtered Deals
  const filteredDeals = useMemo(() => {
    return [...deals].sort((a, b) => Number(b.pinned) - Number(a.pinned)).filter((deal) => {
      // 1. Search Query (title, company/client, contactPerson, owner, notes)
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase().trim()
        const matchTitle = deal.title?.toLowerCase().includes(q)
        const matchClient = deal.client?.toLowerCase().includes(q)
        const matchContact = deal.contactPerson?.toLowerCase().includes(q)
        const matchOwner = deal.owner?.toLowerCase().includes(q)
        const matchNotes = deal.notes?.toLowerCase().includes(q)
        if (!matchTitle && !matchClient && !matchContact && !matchOwner && !matchNotes) {
          return false
        }
      }

      // 2. Assignee / Owner Filter
      if (assigneeFilter !== 'all') {
        if ((deal.owner || 'Unassigned').toLowerCase() !== assigneeFilter.toLowerCase()) {
          return false
        }
      }

      // 3. Stage Filter
      if (stageFilter !== 'all') {
        if ((deal.stage || '').toLowerCase() !== stageFilter.toLowerCase()) {
          return false
        }
      }

      // 4. Priority Filter
      if (priorityFilter !== 'all') {
        const p = deal.priority || 'Normal'
        if (p.toLowerCase() !== priorityFilter.toLowerCase()) {
          return false
        }
      }

      // 5. Pin Filter
      if (pinFilter === 'pinned' && !deal.pinned) {
        return false
      }
      if (pinFilter === 'unpinned' && deal.pinned) {
        return false
      }

      // 6. Preset Views
      if (activeView === 'pinned') {
        if (!deal.pinned) return false
      } else if (activeView === 'won') {
        if (deal.stage?.toLowerCase() !== 'closed won') return false
      } else if (activeView === 'high-value') {
        if ((deal.value || 0) < 50000) return false
      } else if (activeView === 'high-prob') {
        if ((deal.probability || 0) < 70) return false
      } else if (activeView === 'in-progress') {
        const s = deal.stage?.toLowerCase()
        if (s === 'closed won' || s === 'closed lost') return false
      }

      return true
    })
  }, [deals, searchQuery, assigneeFilter, stageFilter, priorityFilter, pinFilter, activeView])

  const hasActiveFilters = Boolean(
    searchQuery || assigneeFilter !== 'all' || stageFilter !== 'all' || priorityFilter !== 'all' || pinFilter !== 'all' || activeView !== 'all'
  )

  const handleClearFilters = () => {
    setSearchQuery('')
    setAssigneeFilter('all')
    setStageFilter('all')
    setPriorityFilter('all')
    setPinFilter('all')
    setActiveView('all')
  }

  const handleCreateProject = async (deal: Deal) => {
    setCreatingDealId(deal.id)
    try {
      const res = await createProjectFromDeal(deal, organization?.id)
      if (res.success && res.project) {
        setLastCreatedProjectId(res.project.id)
        setNotification(`Space Project "${res.project.name}" [${res.project.key}] created successfully for won deal!`)
        await refreshLinkedProjects()
      } else {
        if (res.error?.includes('already created')) {
          setNotification(res.error)
          await refreshLinkedProjects()
          toast(res.error)
        } else {
          toast.error(res.error || 'Could not create the project.')
        }
      }
    } finally {
      setCreatingDealId(null)
    }
  }

  const handleCardClick = (deal: Deal) => {
    setSelectedDeal(deal as unknown as DealDetailData)
    setIsDetailModalOpen(true)
  }

  // Deep-link from a notification click (?dealId=<id>, see AppLayout.tsx) -
  // fetched directly rather than found in the already-loaded list above,
  // since the target deal may not be on the current page.
  useEffect(() => {
    const dealId = searchParams.get('dealId')
    if (!dealId) return
    let cancelled = false
    api.get<any>(`/deals/${dealId}`).then((res) => {
      if (cancelled) return
      const raw = res?.data || res
      if (raw && raw._id) {
        handleCardClick(mapRawDeal(raw) as unknown as Deal)
      } else {
        toast.error('Could not open that deal — it may not be assigned to you.')
      }
    }).catch((err: any) => {
      if (cancelled) return
      console.error('Failed to load deal for deep-link:', err)
      toast.error(err?.response?.data?.message || 'Could not open that deal')
    })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams])

  const handleDealUpdate = (updated: DealDetailData) => {
    setDeals((prev) =>
      prev.map((d) => (d.id === updated.id ? { ...d, ...updated } : d))
    )
    setSelectedDeal(updated)
  }

  // After a bulk update in the table: apply what changed to the loaded deals.
  const handleDealsChanged = (changes: Array<{ id: string; changes: Partial<Deal> }>) => {
    const byId = new Map(changes.map((c) => [c.id, c.changes]))
    setDeals((prev) => prev.map((d) => (byId.has(d.id) ? { ...d, ...byId.get(d.id) } : d)))
  }

  const handleDealCreated = (newDeal: DealDetailData) => {
    setDeals((prev) => [newDeal as unknown as Deal, ...prev])
    setNotification(`Deal "${newDeal.title}" created and saved to database! It will reflect on Active Deals and Pipeline.`)
    setTimeout(() => setNotification(null), 5000)
  }

  return (
    <div className="space-y-6 max-w-full mx-auto">
      {/* Toast Notification */}
      {notification && (
        <div className="p-4 rounded-xl border border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 text-xs flex items-center justify-between shadow-sm animate-in fade-in slide-in-from-top-2 duration-200">
          <div className="flex items-center gap-2 font-medium">
            <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0" />
            <span>{notification}</span>
          </div>
          <div className="flex items-center gap-3">
            {lastCreatedProjectId && (
              <Link
                to={`/spaces/${lastCreatedProjectId}`}
                className="font-bold underline hover:no-underline text-xs"
              >
                View Space →
              </Link>
            )}
            <button
              onClick={() => {
                setNotification(null)
                setLastCreatedProjectId(null)
              }}
              className="text-muted-foreground hover:text-foreground cursor-pointer"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3.5">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-tr from-[#6825F5] to-[#7C3AED] text-white shadow-sm shadow-purple-200 transition-all hover:shadow-md hover:shadow-purple-300/50"
            aria-label="Back"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-foreground">Deals</h1>
            <p className="text-sm text-muted-foreground">Manage deal pipeline stages, revenue probabilities, and close timelines</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => setIsImportModalOpen(true)} className="gap-1.5 shadow-xs">
            <FileSpreadsheet className="h-4 w-4" /> Import Excel
          </Button>
          <Button size="sm" onClick={() => setIsAddModalOpen(true)} className="gap-1.5 shadow-xs">
            <Plus className="h-4 w-4 mr-1.5" /> Add Deal
          </Button>
        </div>
      </div>

      {/* Subsections Navigation */}
      <div className="flex items-center gap-2 border-b border-border pb-3">
        <Link
          to="/app/deals"
          className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-primary text-primary-foreground shadow-sm flex items-center gap-1.5"
        >
          <BadgeDollarSign className="h-3.5 w-3.5" /> Active Deals
        </Link>
        <Link
          to="/app/deals/pipeline"
          className="px-3 py-1.5 text-xs font-medium rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground flex items-center gap-1.5 transition-colors"
        >
          <Kanban className="h-3.5 w-3.5" /> Pipeline
        </Link>
        <Link
          to="/app/deals/timeline"
          className="px-3 py-1.5 text-xs font-medium rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground flex items-center gap-1.5 transition-colors"
        >
          <Clock className="h-3.5 w-3.5" /> Timeline
        </Link>
        <Link
          to="/app/deals/archive"
          className="px-3 py-1.5 text-xs font-medium rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground flex items-center gap-1.5 transition-colors"
        >
          <Archive className="h-3.5 w-3.5" /> Archive
        </Link>
      </div>

      {/* Filter Toolbar (Search deals + All assignees + Views + All stages + All priorities) */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 pt-1">
        {/* Left Side: Search + All assignees + Views */}
        <div className="flex items-center gap-2.5 flex-wrap flex-1">
          {/* Search Box */}
          <div className="relative w-full sm:w-72 md:w-80">
            <Search className="h-3.5 w-3.5 absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground/70" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search deals by title, company, contact..."
              className="w-full pl-9 pr-7 py-1.5 text-xs rounded-full border border-border/80 bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all shadow-2xs"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground p-0.5"
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </div>

          {/* All assignees Dropdown */}
          <div className="relative">
            <select
              value={assigneeFilter}
              onChange={(e) => setAssigneeFilter(e.target.value)}
              className="appearance-none rounded-full border border-border/80 bg-background pl-3.5 pr-8 py-1.5 text-xs font-medium text-foreground shadow-2xs focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all cursor-pointer"
            >
              <option value="all">All assignees</option>
              {uniqueAssignees.map((owner) => (
                <option key={owner} value={owner}>
                  {owner}
                </option>
              ))}
            </select>
            <ChevronDown className="h-3 w-3 absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          </div>

          {/* Views Preset Dropdown */}
          <div className="relative" ref={viewsDropdownRef}>
            <button
              type="button"
              onClick={() => setIsViewsOpen((prev) => !prev)}
              className={`inline-flex items-center gap-1.5 rounded-full border border-border/80 bg-background px-3.5 py-1.5 text-xs font-medium text-foreground hover:bg-muted/40 transition-colors shadow-2xs ${
                activeView !== 'all' ? 'border-primary text-primary ring-1 ring-primary/20' : ''
              }`}
            >
              <Bookmark className="h-3.5 w-3.5 text-muted-foreground" />
              <span>Views</span>
              <ChevronDown
                className={`h-3 w-3 text-muted-foreground transition-transform ${
                  isViewsOpen ? 'rotate-180' : ''
                }`}
              />
            </button>

            {isViewsOpen && (
              <div className="absolute left-0 mt-1.5 w-56 rounded-xl border border-border/80 bg-popover p-1.5 shadow-lg z-30 text-xs animate-in fade-in-50 zoom-in-95">
                <div className="px-2 py-1 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
                  Saved Views
                </div>
                {[
                  { id: 'all', label: 'All Deals (Default)' },
                  { id: 'pinned', label: '📌 Pinned Deals' },
                  { id: 'won', label: 'Closed Won' },
                  { id: 'high-value', label: 'High Value (≥ ₹50k)' },
                  { id: 'high-prob', label: 'High Probability (≥ 70%)' },
                  { id: 'in-progress', label: 'Active Pipeline (Open)' },
                ].map((v) => (
                  <button
                    key={v.id}
                    type="button"
                    onClick={() => {
                      setActiveView(v.id)
                      setIsViewsOpen(false)
                    }}
                    className={`w-full flex items-center justify-between px-2.5 py-1.5 rounded-lg text-left transition-colors ${
                      activeView === v.id
                        ? 'bg-primary/10 text-primary font-semibold'
                        : 'hover:bg-muted text-foreground'
                    }`}
                  >
                    <span>{v.label}</span>
                    {activeView === v.id && <Check className="h-3.5 w-3.5" />}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right Side: Filters (Sliders icon + All stages + All priorities + Reset) */}
        <div className="flex items-center gap-2 flex-wrap">
          <div className="text-muted-foreground p-1">
            <SlidersHorizontal className="h-4 w-4" />
          </div>

          {/* All stages Dropdown */}
          <div className="relative">
            <select
              value={stageFilter}
              onChange={(e) => setStageFilter(e.target.value)}
              className="appearance-none rounded-full border border-border/80 bg-background pl-3.5 pr-8 py-1.5 text-xs font-medium text-foreground shadow-2xs focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all cursor-pointer"
            >
              <option value="all">All stages</option>
              {dealStages.map(({ name: st }) => (
                <option key={st} value={st}>
                  {st}
                </option>
              ))}
            </select>
            <ChevronDown className="h-3 w-3 absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          </div>

          {/* All priorities Dropdown */}
          <div className="relative">
            <select
              value={priorityFilter}
              onChange={(e) => setPriorityFilter(e.target.value)}
              className="appearance-none rounded-full border border-border/80 bg-background pl-3.5 pr-8 py-1.5 text-xs font-medium text-foreground shadow-2xs focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all cursor-pointer"
            >
              <option value="all">All priorities</option>
              {PRIORITY_OPTIONS.map((pr) => (
                <option key={pr} value={pr}>
                  {pr}
                </option>
              ))}
            </select>
            <ChevronDown className="h-3 w-3 absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          </div>

          {/* Pin Filter */}
          <div className="relative">
            <select
              value={pinFilter}
              onChange={(e) => setPinFilter(e.target.value as 'all' | 'pinned' | 'unpinned')}
              className={`appearance-none rounded-full border bg-background pl-3.5 pr-8 py-1.5 text-xs font-medium shadow-2xs focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all cursor-pointer ${
                pinFilter === 'pinned'
                  ? 'border-amber-500/80 bg-amber-500/10 text-amber-600 dark:text-amber-400 font-semibold ring-1 ring-amber-500/30'
                  : pinFilter === 'unpinned'
                  ? 'border-primary/80 bg-primary/10 text-primary font-semibold'
                  : 'border-border/80 text-foreground'
              }`}
            >
              <option value="all">Pin: All</option>
              <option value="pinned">📌 Pinned only</option>
              <option value="unpinned">Unpinned only</option>
            </select>
            <ChevronDown className="h-3 w-3 absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          </div>

          {/* View switch: Table / Cards (cards only on narrow screens) */}
          {!isNarrow && (
            <div role="group" aria-label="Deals view" className="flex items-center rounded-full border border-border/80 bg-background p-0.5 shadow-2xs">
              <button
                type="button"
                aria-pressed={effectiveView === 'table'}
                title="Table view"
                onClick={() => chooseViewMode('table')}
                className={`flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium transition-colors ${
                  effectiveView === 'table' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <List className="h-3.5 w-3.5" /> Table
              </button>
              <button
                type="button"
                aria-pressed={effectiveView === 'cards'}
                title="Card view"
                onClick={() => chooseViewMode('cards')}
                className={`flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium transition-colors ${
                  effectiveView === 'cards' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <LayoutGrid className="h-3.5 w-3.5" /> Cards
              </button>
            </div>
          )}

          {/* Clear Filters Button */}
          {hasActiveFilters && (
            <button
              type="button"
              onClick={handleClearFilters}
              className="text-xs text-muted-foreground hover:text-foreground font-medium underline underline-offset-2 px-1 transition-colors"
            >
              Reset
            </button>
          )}
        </div>
      </div>

      {/* Deals Grid */}
      {loading ? (
        <div className="py-20 text-center flex flex-col items-center justify-center">
          <RefreshCw className="h-7 w-7 text-primary animate-spin mb-3" />
          <p className="text-sm text-muted-foreground">Loading active deals from database...</p>
        </div>
      ) : filteredDeals.length === 0 ? (
        <div className="py-16 text-center border border-dashed border-border rounded-2xl p-8">
          <SlidersHorizontal className="h-10 w-10 text-muted-foreground/50 mx-auto mb-2" />
          <h3 className="text-base font-semibold text-foreground">No deals match your filter criteria</h3>
          <p className="text-xs text-muted-foreground mb-4">
            {hasActiveFilters
              ? 'Try clearing or adjusting your search term, assignee, stage, priority, or pin filter'
              : 'Add your first sales deal to populate the pipeline'}
          </p>
          {hasActiveFilters ? (
            <Button size="sm" variant="outline" onClick={handleClearFilters}>
              Reset All Filters
            </Button>
          ) : (
            <Button size="sm" onClick={() => setIsAddModalOpen(true)}>
              <Plus className="h-4 w-4 mr-1.5" /> Add Deal
            </Button>
          )}
        </div>
      ) : effectiveView === 'table' ? (
        <DealsTable
          deals={filteredDeals}
          stages={dealStages}
          assignableUsers={assignableUsers}
          isDealWon={isDealWon}
          linkedProjects={linkedProjects}
          canCreateSpace={canCreateSpace}
          canViewSpace={canViewSpace}
          creatingDealId={creatingDealId}
          onOpen={handleCardClick}
          onCreateQuotation={handleCreateQuotation}
          onCreateSpace={handleCreateProject}
          onDealsChanged={handleDealsChanged}
          resetKey={JSON.stringify([searchQuery, assigneeFilter, stageFilter, priorityFilter, pinFilter, activeView])}
        />
      ) : (
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {filteredDeals.map((deal) => (
            <Card
              key={deal.id}
              onClick={() => handleCardClick(deal)}
              className="hover:border-primary/60 transition-all flex flex-col justify-between group"
            >
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between gap-2">
                  <Badge className="bg-emerald-600 hover:bg-emerald-700 text-white text-[10px] font-bold">
                    {isDealWon(deal.stage) ? 'Closed Won' : deal.stage}
                  </Badge>
                  <span className="text-xs font-semibold text-emerald-600 dark:text-emerald-400">{deal.probability}%</span>
                </div>
                <div className="flex items-center justify-between gap-2 pt-1">
                  <CardTitle className="text-base font-bold text-foreground truncate group-hover:text-primary transition-colors" title={deal.title}>
                    {deal.title}
                  </CardTitle>
                  {deal.pinned && (
                    <span className="text-amber-500 flex-shrink-0" title="Pinned">
                      <svg viewBox="0 0 24 24" fill="currentColor" className="h-3.5 w-3.5">
                        <path d="M16 3a1 1 0 0 1 1 1v1.586l2.293 2.293a1 1 0 0 1-.707 1.707H16.5l-.5 6.5a1 1 0 0 1-1.96.18L13.5 10h-3.5l-.5 5.5a1 1 0 0 1-1.96.18L7.5 10H5.414a1 1 0 0 1-.707-1.707L7 6.586V4a1 1 0 0 1 1-1h8Z"/>
                      </svg>
                    </span>
                  )}
                </div>
                <p className="text-xs text-muted-foreground truncate">{deal.client}</p>
                {deal.services && deal.services.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {groupServicesByCategory(deal.services).map(({ category, services: svcs }) => (
                      <span
                        key={category.category}
                        className={`text-[10px] px-1.5 py-0.5 rounded font-medium border ${category.badgeClass} truncate max-w-[170px]`}
                        title={`${category.category}: ${svcs.join(', ')}`}
                      >
                        <span className="font-bold opacity-80 mr-1">{category.departmentLabel}:</span>
                        <span>{svcs[0]}{svcs.length > 1 ? ` +${svcs.length - 1}` : ''}</span>
                      </span>
                    ))}
                  </div>
                )}
              </CardHeader>
              <CardContent className="space-y-3 pt-1">
                <div className="flex items-baseline justify-between">
                  <span className="text-2xl font-extrabold text-foreground">₹{deal.value?.toLocaleString()}</span>
                  <span className="text-[11px] text-muted-foreground">Target: {deal.closeDate || 'Q4 2026'}</span>
                </div>
                <div className="w-full h-1.5 rounded-full bg-muted overflow-hidden">
                  <div className="h-full bg-emerald-500" style={{ width: `${deal.probability}%` }} />
                </div>
                <div className="flex items-center justify-between text-xs text-muted-foreground pt-1 border-t border-border/40">
                  <span>Owner: {deal.owner || 'Unassigned'}</span>
                  {deal.priority && (
                    <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-muted/60 text-muted-foreground">
                      {deal.priority}
                    </span>
                  )}
                </div>

                <div className="space-y-1.5 mt-2">
                  {deal.quotation ? (
                    <DealQuotationLink quotation={deal.quotation} className="w-full h-8" />
                  ) : (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={(e) => {
                        e.stopPropagation()
                        handleCreateQuotation(deal)
                      }}
                      className="w-full border-emerald-500/40 hover:bg-emerald-50 text-emerald-700 dark:text-emerald-300 dark:hover:bg-emerald-950/40 text-xs font-semibold flex items-center justify-center gap-1.5 shadow-2xs h-8 transition-colors"
                    >
                      <FileText className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />
                      <span>Create Quotation</span>
                    </Button>
                  )}

                  {linkedProjects.has(deal.id) ? (
                    canViewSpace && (
                    <Link
                      to={`/spaces/${linkedProjects.get(deal.id)?.id}`}
                      onClick={(e) => e.stopPropagation()}
                      className="w-full py-1.5 px-2.5 rounded-lg bg-emerald-50 hover:bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300 dark:hover:bg-emerald-900/60 border border-emerald-300 dark:border-emerald-800 text-xs font-semibold flex items-center justify-between transition-colors shadow-2xs"
                    >
                      <span className="flex items-center gap-1.5">
                        <ExternalLink className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />
                        <span>View Space</span>
                      </span>
                      <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-emerald-100/80 dark:bg-emerald-900/60 text-emerald-800 dark:text-emerald-200">
                        {linkedProjects.get(deal.id)?.key}
                      </span>
                    </Link>
                    )
                  ) : (
                    canCreateSpace && (
                    <Button
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation()
                        handleCreateProject(deal)
                      }}
                      disabled={creatingDealId === deal.id}
                      className="w-full bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold flex items-center justify-center gap-1.5 shadow-sm h-8"
                    >
                      {creatingDealId === deal.id ? (
                        <>
                          <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                          <span>Creating Space...</span>
                        </>
                      ) : (
                        <>
                          <Rocket className="h-3.5 w-3.5" />
                          <span>Create Space / Project</span>
                        </>
                      )}
                    </Button>
                    )
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Deal Detail Modal */}
      <DealDetailModal
        isOpen={isDetailModalOpen}
        onClose={() => setIsDetailModalOpen(false)}
        deal={selectedDeal}
        onUpdate={handleDealUpdate}
        onDeleted={() => fetchDeals()}
        currentSectionName="Active Deals"
        currentSectionHref="/app/deals"
      />

      {/* Add Deal Modal */}
      <AddDealModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onDealCreated={handleDealCreated}
      />

      {/* Import deals from a spreadsheet */}
      <ImportExcelModal
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        kind="deals"
        onImported={(result) => {
          toast.success(`${result.imported} deal${result.imported === 1 ? '' : 's'} added from the spreadsheet.`)
          fetchDeals()
        }}
      />
    </div>
  )
}
